import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { del, get, post } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import { toast } from "../../Utils/Helper/toast";

interface MonitorAccount {
  customer_id?: string;
  account_id?: string;
  id?: string;
  name?: string;
  account_name?: string;
  primary_sponsors?: string | null;
  [key: string]: any;
}

interface ExecutivePortfolioAccount {
  account_id?: string;
  customer_id?: string;
  id?: string;
  name?: string;
  account_name?: string;
  relationship_type?: "primary" | "secondary" | "extra_sponsor" | string | null;
  [key: string]: any;
}

interface MonitorAccountsPayload {
  account_ids: string[];
}

const normalizeMonitorAccounts = (payload: any): MonitorAccount[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeExecutiveAccounts = (
  payload: any
): ExecutivePortfolioAccount[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

interface AlwaysOnChannelState {
  monitorAccounts: {
    items: MonitorAccount[];
    loading: boolean;
    saving: boolean;
    deleting: boolean;
    error: string | null;
  };
  executiveAccounts: {
    items: ExecutivePortfolioAccount[];
    loading: boolean;
    error: string | null;
  };
}

const initialState: AlwaysOnChannelState = {
  monitorAccounts: {
    items: [],
    loading: false,
    saving: false,
    deleting: false,
    error: null,
  },
  executiveAccounts: {
    items: [],
    loading: false,
    error: null,
  },
};

export const fetchMonitoredAccounts = createAsyncThunk(
  "alwaysOnChannel/fetchMonitoredAccounts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.monitorAccounts());
      return response;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch monitored accounts";
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchExecutiveAccounts = createAsyncThunk(
  "alwaysOnChannel/fetchExecutiveAccounts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.executiveAccounts());
      return response;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to fetch executive extra-sponsor accounts";
      return rejectWithValue(errorMessage);
    }
  }
);

export const saveMonitoredAccounts = createAsyncThunk(
  "alwaysOnChannel/saveMonitoredAccounts",
  async (payload: MonitorAccountsPayload, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.monitorAccounts(), payload);
      return response;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save monitored accounts";
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteMonitoredAccounts = createAsyncThunk(
  "alwaysOnChannel/deleteMonitoredAccounts",
  async (monitorIds: string[], { rejectWithValue }) => {
    try {
      const response = await del(API_URLS.monitorAccounts(), {
        ids: monitorIds,
      });
      return { monitorIds, response };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete monitored accounts";
      return rejectWithValue(errorMessage);
    }
  }
);

const alwaysOnChannelSlice = createSlice({
  name: "alwaysOnChannel",
  initialState,
  reducers: {
    clearMonitorAccountsError: state => {
      state.monitorAccounts.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchMonitoredAccounts.pending, state => {
        state.monitorAccounts.loading = true;
        state.monitorAccounts.error = null;
      })
      .addCase(fetchMonitoredAccounts.fulfilled, (state, action) => {
        state.monitorAccounts.loading = false;
        state.monitorAccounts.items = normalizeMonitorAccounts(action.payload);
        state.monitorAccounts.error = null;
      })
      .addCase(fetchMonitoredAccounts.rejected, (state, action) => {
        state.monitorAccounts.loading = false;
        state.monitorAccounts.error = action.payload as string;
      })
      .addCase(fetchExecutiveAccounts.pending, state => {
        state.executiveAccounts.loading = true;
        state.executiveAccounts.error = null;
      })
      .addCase(fetchExecutiveAccounts.fulfilled, (state, action) => {
        state.executiveAccounts.loading = false;
        state.executiveAccounts.items = normalizeExecutiveAccounts(
          action.payload
        );
        state.executiveAccounts.error = null;
      })
      .addCase(fetchExecutiveAccounts.rejected, (state, action) => {
        state.executiveAccounts.loading = false;
        state.executiveAccounts.error = action.payload as string;
      })
      .addCase(saveMonitoredAccounts.pending, state => {
        state.monitorAccounts.saving = true;
        state.monitorAccounts.error = null;
      })
      .addCase(saveMonitoredAccounts.fulfilled, (state, action) => {
        state.monitorAccounts.saving = false;
        state.monitorAccounts.error = null;
        toast.success(action.payload?.response?.message);
      })
      .addCase(saveMonitoredAccounts.rejected, (state, action) => {
        state.monitorAccounts.saving = false;
        state.monitorAccounts.error = action.payload as string;
        toast.error(
          (action.payload as string) || "Failed to save monitored accounts"
        );
      })
      .addCase(deleteMonitoredAccounts.pending, state => {
        state.monitorAccounts.deleting = true;
        state.monitorAccounts.error = null;
      })
      .addCase(deleteMonitoredAccounts.fulfilled, (state, action) => {
        state.monitorAccounts.deleting = false;
        state.monitorAccounts.error = null;
        const deletedIds = new Set(
          (action.payload.monitorIds || []).map(id => String(id))
        );
        state.monitorAccounts.items = state.monitorAccounts.items.filter(
          item => !deletedIds.has(String(item.id || ""))
        );
        toast.success(
          action.payload?.response?.response?.message ||
            "Monitored account deleted successfully"
        );
      })
      .addCase(deleteMonitoredAccounts.rejected, (state, action) => {
        state.monitorAccounts.deleting = false;
        state.monitorAccounts.error = action.payload as string;
        toast.error(
          (action.payload as string) || "Failed to delete monitored accounts"
        );
      });
  },
});

export const { clearMonitorAccountsError } = alwaysOnChannelSlice.actions;
export default alwaysOnChannelSlice.reducer;
